import axios from "../../../api/axios";
import { api } from "./constants";

const { application_submit_url: APPLICANT_SUBMIT_URL } = api.endpoints;

const MIN_DOCUMENTS_PER_APPLICANT = 2;

const isUploadedDocument = (doc) => {
    const url = doc?.url;
    return typeof url === "string" && url.trim().length > 0;
};

const extractDocumentUrls = (applicant) => {
    if (!Array.isArray(applicant?.documents)) return [];
    return applicant.documents.filter(isUploadedDocument).map((doc) => doc.url);
};

const validateApplicantDocuments = (applicants) => {
    if (!Array.isArray(applicants) || applicants.length === 0) {
        throw new Error("At least one applicant is required before submitting.");
    }
    applicants.forEach((applicant, index) => {
        const docs = Array.isArray(applicant?.documents) ? applicant.documents : [];
        if (docs.length < MIN_DOCUMENTS_PER_APPLICANT) {
            throw new Error(
                `Applicant ${index + 1} must attach at least ${MIN_DOCUMENTS_PER_APPLICANT} identification documents before submitting.`
            );
        }
        if (!docs.every(isUploadedDocument)) {
            throw new Error(
                `Applicant ${index + 1} has one or more attachments that have not finished uploading. Please wait for uploads to complete or re-attach the file.`
            );
        }
    });
};

const createApplication = async (values) => {
    const body = {
        main: {
            city: values.main.city,
            property: values.main.property,
            propertyID: values.main.propertyID,
            date: values.main.date,
            layout: values.main.layout,
            layoutID: values.main.layoutID,
            source: values.main.source,
        },
        applicants: values.applicants.map(applicant => ({
            ...applicant,
            previousAddressInformation: {
                previousAddress: applicant.previousAddressInformation.previousAddress,
                howLongAtPreviousAddress: applicant.previousAddressInformation.howLongAtPreviousAddress,
                previousLandloardName: applicant.previousAddressInformation.previousLandlordName,
                previousLandloardPhone: applicant.previousAddressInformation.previousLandlordPhone,
            },
            currentLandloard: applicant.currentLandlord,
            documents: extractDocumentUrls(applicant),
        })),
        residePersons: values.residePersons.map(resident => ({
            ...resident,
            name: resident.firstname + resident.firstname,
        })),
        emergencyContacts: values.emergencyContacts,
        signatures: values.signatures,
        companyDomain: values.companyDomain,
    };

    if (values.showPets) {
        body.petsInformation = {
            numberOfPets: values.petsInformation.length,
            petsInformation: values.petsInformation.map(pet => ({
                petType: pet.pets,
                breed: pet.breed,
                weight: pet.weight,
                age: pet.age,
            }))
        };
    }

    try {
        const { data } = await axios.post(APPLICANT_SUBMIT_URL, body);
        return data.id;
    } catch (error) {
        console.error("Error creating application:", error);
        throw error;
    }
};

const onSubmit = async (values) => {
    try {
        validateApplicantDocuments(values?.applicants);

        const ticket_id = await createApplication(values);
        if (!ticket_id) throw new Error("Failed to create application");

        return { success: true, id: ticket_id };
    } catch (error) {
        return { success: false, message: error.message || "Something went wrong" };
    }
};

export { onSubmit };
